import { SupabaseClient } from "@supabase/supabase-js";
import { BASE_URL, COUNTRY_CODE } from "@utils/constants";
import axios, { AxiosError } from "axios";
import { analytics } from "@components/analytics";
import { authorizedFetcher } from "@utils/fetchers";
import { getCookieValue } from "@utils/helpers";
import { mutate } from "swr";
import { SyntheticEvent } from "react";
import { sendOtpNewUserEmail } from "./otpNewUser";

export async function patchPostCode(supabase: SupabaseClient) {
  const websiteUid = getCookieValue("itk-website-uid");
  const content_language = getCookieValue("NEXT_LOCALE");
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();

  if (!sessionData.session || sessionError) return;

  const user = await authorizedFetcher([
    "account",
    sessionData.session.access_token,
  ]);

  if (user?.postcode) {
    const basketKey = getCookieValue("ASP.NET_SessionId");
    try {
      await axios.patch(
        `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/checkout/postcode`,
        {
          postCode: user?.postcode,
        },
        {
          headers: {
            "content-language": content_language,
            "itk-website-uid": websiteUid,
          },
        }
      );
      // The checkout endpoint can have a brief read-after-write lag right
      // after this patch - a GET immediately afterwards can still come back
      // with the pre-patch postcode (matches what a refresh a moment later
      // then shows correctly). A plain mutate()/revalidate can't fix this:
      // it just triggers another one of these same GETs, which can hit the
      // same lag, and the payment page's own fetch on mount can do the same
      // regardless of what we've put in its cache beforehand. Poll the
      // authenticated endpoint - the same one the payment page reads - until
      // it actually reflects the patch, so navigation only happens once the
      // backend itself is caught up.
      const authedKey: [string, string] = [
        `basket/${basketKey}/checkout`,
        sessionData.session.access_token,
      ];
      let confirmed;
      for (let attempt = 0; attempt < 6; attempt++) {
        const fresh = await authorizedFetcher(authedKey);
        if (fresh?.PostCode === user.postcode) {
          confirmed = fresh;
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 400));
      }

      await mutate(authedKey, confirmed, { revalidate: !confirmed });
      await mutate(`basket/${basketKey}/checkout`);

      return "success";
    } catch (err: AxiosError | any) {
      if (err.response && err.response.status >= 400 && err.status < 500) {
        return { status: "failure", message: err.response.data.message };
      } else {
        console.error(err);

        return {
          status: "failure",
          message: err.response.data.message ?? "Something went wrong",
        };
      }
    }
  }
}

export async function handleOTPUser(
  event: SyntheticEvent<HTMLFormElement>,
  supabase: SupabaseClient,
  signUpHref?: string,
  isCheckout?: boolean
) {
  try {
    const { email } = Object.fromEntries(new FormData(event.currentTarget));
    const { error } = await supabase.auth.signInWithOtp({
      email: email as string,
      options: {
        shouldCreateUser: false,
      },
    });

    if (error) {
      if (error.status === 422) {
        try {
          await sendOtpNewUserEmail({
            email: email as string,
            redirectTo: signUpHref,
            isCheckout,
          });

          return {
            status: "success",
            message: "Email sent",
          };
        } catch (err) {
          return {
            status: "failure",
            message:
              err instanceof Error ? err.message : "Something went wrong",
          };
        }
      }
      throw error;
    }
  } catch {
    return {
      status: "failure",
      message: "Something went wrong",
    };
  }
}

export async function handleSignInUser(
  event: SyntheticEvent<HTMLFormElement>,
  supabase: SupabaseClient
) {
  const { email, password } = Object.fromEntries(
    new FormData(event.currentTarget)
  );

  const { error, data } = await supabase.auth.signInWithPassword({
    email: email as string,
    password: password as string,
  });

  if (error) {
    return { status: "failure", message: error.message };
  }

  if (!error && !data.user.app_metadata["iticket_user_uid"]) {
    await supabase.auth.signOut();
    analytics.reset();

    return { status: "failure", message: "Invalid login details" };
  }

  if (data.user) {
    analytics.identify(
      data?.user?.app_metadata?.iticket_user_uid
        ? data.user.app_metadata.iticket_user_uid.toLowerCase()
        : (email as string),
      {
        id: data?.user?.app_metadata?.iticket_user_uid?.toLowerCase(),
        email: email as string,
      }
    );

    analytics.track("Sign in", {
      iticket_uid: data?.user?.app_metadata?.iticket_user_uid?.toLowerCase(),
      email: email,
      method: "password",
    });
  }

  return {
    status: "success",
    message: "Logged In",
  };
}
